import { useFlag, useNewCode } from '@calendly/feature-check-react'
import { useOnValueChange, usePrevious, useQueryParams } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { PageHeader } from '@calendly/ui/components/page-header'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { isEmpty } from 'lodash-es'
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom'

import { setGetParams } from 'actions/core/navigation'
import { loadTitle } from 'actions/home_bar/scope_dropdown'
import {
  useMeetingsTabInitialLoad,
  useMeetingsTabPageLoad,
} from 'components/home/meetings_tab_initial_load'
import { MeetingsDebugMenu, useForceSSEDisconnect, useRealtimeSSEIntegration } from 'components/meetings'
import { useStickyHeaderScrollOffset } from 'components/meetings/hooks'
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell'
import { useGetUserSettingsQuery } from 'features/availability'
import { BILLING_ENTRYPOINTS, isAvailableFeatureSelector } from 'features/billing'
import { CONTENT_PAGES } from 'features/empty_state'
import {
  AutoShareDiscoveryModal,
  useShouldDisplayAutoShareDiscoveryModal,
  FreeRecapsHeaderAction
} from 'features/meeting_intelligence'
import { useMeetingsRealtimeFeaturesEnabled } from 'features/meetings/hooks'
import { SearchFilterExport } from 'features/meetings/search_filter_export'
import { useRightRail } from 'features/right_rail'
import { URL_STATE_PREFIX } from 'features/scoped_url_state'
import { DismissableNotetakerBanner } from 'features/upgrade_banner'
import { useTrackPageExit, useFeatureFlagEncountered, useTempFeatureFlagEncountered } from 'hooks/analytics_events'
import { useScope } from 'hooks/meetings/use_scope'

import { AddNotetakerModal } from './add_notetaker_modal'
import {
  AllPagesAuthorization,
  AllUsersAuthorization,
  PageAuthorization,
  UserAuthorization,
  UserGroupAuthorization
} from './authorization'
import { MeetingsProvider } from './contexts/MeetingsContext'
import { ErrorList, EmptyFilteredList, PresetEmptyMeetings } from './error_states'
import { useMeetingsSearchData } from './hooks/search_data/use_search_data'
import { useMeetingsViewDate } from './hooks/use_meetings_view_date'
import { useRescheduledToast } from './hooks/use_rescheduled_toast'
import { useSelectedView } from './hooks/use_selected_view'
import { useStableDisplayedMeetingCount } from './hooks/use_stable_displayed_meeting_count'
import { useUpcomingPreset } from './hooks/use_upcoming_preset'
import { MeetingList } from './meeting_list'
import { MeetingsPageHeaderActions } from './meetings_page_header_actions'
import { MeetingsPageOnboardingTour } from './meetings_page_onboarding_tour'
import { setOpenMeetingDate, setScrollAnchorDate } from './meetings_page_slice'
import { isFilterSet } from './utils'
import { EMPTY_STATE_TYPES, EmptyState } from '../../components/empty_state'
import { MEETINGS_PRESET_IDS, normalizePersistedSchedulingMethods } from '../../constants/meetings'
import { MEETINGS_TAB_BASE_PATH_LEGACY, MEETINGS_TAB_BASE_PATH_REBRAND } from '../../hooks/home/meetings_tab_paths'
import { useHomeTabs } from '../../hooks/home/tabs'
import { useFilters } from '../../hooks/meetings/filters'
import { titleSelector } from '../../selectors/home_bar'

const MEETINGS_PAGE_TRACK_EXIT_MATCH = {
  path: [MEETINGS_TAB_BASE_PATH_LEGACY, MEETINGS_TAB_BASE_PATH_REBRAND],
  exact: false,
}

/**
 * Provided by MeetingsPageContentWithRealtime / WithoutRealtime.
 * Carries meetingsData plus shared args (scope, showBuffers, defaultSchedulingFilters) so
 * MeetingsPageContentBody does not duplicate useScope/useGetUserSettingsQuery/derivations.
 */
const MeetingsPageSearchDataContext = createContext(null)

const InnerContent = ({
  data: meetings,
  hasNext,
  hasPrevious,
  headerRef,
  hasLoadAttemptStarted = false,
  isError,
  isLoading,
  isNextLoading,
  isPreviousLoading,
  isSuccess,
  retrieveNextPage,
  retrievePreviousPage,
  selectedMeetingId,
  showBuffers,
  filterDefaults,
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  onVisibleDateRangeChange,
  onScrollAnchorDateChange,
  scrollToDateTarget,
}) => {
  const { getTabPathByName } = useHomeTabs()
  const { getFilterParams, applyFilterParams } = useFilters()
  const { activePreset } = useUpcomingPreset()
  const filterParams = getFilterParams(filterDefaults)

  const hasLoadAttemptCompleted = hasLoadAttemptStarted && !isLoading

  if ((isError || !isSuccess) && hasLoadAttemptCompleted) {
    return <ErrorList />
  }

  if (isEmpty(meetings) && isFilterSet(filterParams) && !activePreset) {
    return <EmptyFilteredList apply={applyFilterParams} filterDefaults={filterDefaults} />
  }

  // Date presets (today / this week / last week) with zero results show the preset empty
  // message. The default upcoming preset on a brand-new account still uses the marketing
  // empty state so legacy analytics/feature specs keep working when the flag is on.
  if (isEmpty(meetings) && activePreset && activePreset !== MEETINGS_PRESET_IDS.UPCOMING) {
    return <PresetEmptyMeetings />
  }

  if (isEmpty(meetings) && !isFilterSet(filterParams)) {
    return (
      <EmptyState
        type={EMPTY_STATE_TYPES.leftRight}
        contentKey={CONTENT_PAGES.meetings}
        options={{ url: getTabPathByName('event_types') }}
      />
    )
  }

  return (
    <MeetingList
      meetings={meetings}
      retrieveNextPage={retrieveNextPage}
      retrievePreviousPage={retrievePreviousPage}
      selectedMeetingId={selectedMeetingId}
      showBuffers={showBuffers}
      hasNext={hasNext}
      hasPrevious={hasPrevious}
      isNextLoading={isNextLoading}
      isPreviousLoading={isPreviousLoading}
      headerRef={headerRef}
      onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback}
      onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback}
      onVisibleDateRangeChange={onVisibleDateRangeChange}
      onScrollAnchorDateChange={onScrollAnchorDateChange}
      scrollToDateTarget={scrollToDateTarget}
    />
  )
}

// Builds useMeetingsSearchData args from scope/settings; used by both realtime and legacy wrappers.
function useSearchDataArgs() {
  const { scopeType, scopeId } = useScope()
  const { data: userSettings } = useGetUserSettingsQuery()
  const meetingPageSettings = userSettings?.meetingsPageSettings || {}
  const defaultSchedulingFilters = useMemo(
    () => ({
      scheduling_methods: normalizePersistedSchedulingMethods(
        meetingPageSettings.schedulingMethods
      )
    }),
    [meetingPageSettings.schedulingMethods]
  )
  const showBuffers = meetingPageSettings.showBuffers ?? true
  // When the Upcoming preset is active, the meetings page loads today + upcoming only
  // and paginates forward; the past query and "previous" pagination are disabled.
  // Today / This week / Last week presets load a single list query for that range.
  const { presetActive, presetPeriod, presetFlagOn } = useUpcomingPreset()
  // When the user picks a date in the selector, the untoggled feed re-centers on it (past +
  // future around the date). `viewDate` is only set while no preset is active.
  const { viewDate } = useMeetingsViewDate()
  return {
    scopeType,
    scopeId,
    showBuffers,
    defaultFilters: defaultSchedulingFilters,
    forwardOnly: presetFlagOn && presetActive,
    presetPeriod: presetFlagOn ? presetPeriod : null,
    pivotDate: viewDate,
  }
}

/** First list fetch only — scope/filter refetches must not re-trigger the tab shell overlay. */
function useMeetingsInitialListLoad(isLoading) {
  const hasLoadAttemptStartedRef = useRef(false)
  const hasInitialLoadCompletedRef = useRef(false)

  useEffect(() => {
    if (isLoading) {
      hasLoadAttemptStartedRef.current = true
    } else if (hasLoadAttemptStartedRef.current) {
      hasInitialLoadCompletedRef.current = true
    }
  }, [isLoading])

  const isInitialListLoading = isLoading && !hasInitialLoadCompletedRef.current
  useMeetingsTabInitialLoad('list', isInitialListLoading)

  return {
    hasLoadAttemptStarted: hasLoadAttemptStartedRef.current,
    isInitialListLoading,
  }
}

/**
 * The Contact Details Page has a meetings feed that tries to mirror the meetings page.
 * Changes to the meetings page should be reflected in the contact details page.
 * TODO: Refactor the meetings feed to use the meetings components and remove the duplicate code.
 * TODO: Add tests for the meetings feed.
 *
 * ref: javascripts/features/crm/contact_page/feed/meetings_feed.js
 */

/** Realtime path: SSE + polling enabled, uses search API for list data + visible-range polling. */
const MeetingsPageContentWithRealtime = () => {
  const args = useSearchDataArgs()
  const forceSSEDisconnect = useForceSSEDisconnect()
  const { connectionState } = useRealtimeSSEIntegration({ enabled: !forceSSEDisconnect })
  const [visibleDateRange, setVisibleDateRange] = useState({ start: null, end: null })
  const meetingsData = useMeetingsSearchData({
    ...args,
    pollingConfig: { isPollingEnabled: true },
    visibleRangePollingEnabled: true,
    visibleDateRange,
  })
  const isListLoading = meetingsData?.isLoading ?? false
  const { hasLoadAttemptStarted, isInitialListLoading } = useMeetingsInitialListLoad(isListLoading)
  const onVisibleDateRangeChange = useCallback((range) => {
    setVisibleDateRange(range || { start: null, end: null })
  }, [])
  const contextValue = useMemo(
    () => ({
      meetingsData,
      scopeType: args.scopeType,
      scopeId: args.scopeId,
      showBuffers: args.showBuffers,
      defaultSchedulingFilters: args.defaultFilters,
      onVisibleDateRangeChange,
      connectionState,
      hasLoadAttemptStarted,
    }),
    [
      meetingsData,
      args.scopeType,
      args.scopeId,
      args.showBuffers,
      args.defaultFilters,
      onVisibleDateRangeChange,
      connectionState,
      hasLoadAttemptStarted,
    ]
  )
  return (
    <MeetingsPageSearchDataContext.Provider value={contextValue}>
      {!isInitialListLoading && <MeetingsPageContentBody />}
    </MeetingsPageSearchDataContext.Provider>
  )
}

/** Legacy path: no SSE, no polling, uses search API for list data. */
const MeetingsPageContentWithoutRealtime = () => {
  const args = useSearchDataArgs()
  const meetingsData = useMeetingsSearchData({
    ...args,
    pollingConfig: { isPollingEnabled: false },
  })
  const isListLoading = meetingsData?.isLoading ?? false
  const { hasLoadAttemptStarted, isInitialListLoading } = useMeetingsInitialListLoad(isListLoading)
  const contextValue = useMemo(
    () => ({
      meetingsData,
      scopeType: args.scopeType,
      scopeId: args.scopeId,
      showBuffers: args.showBuffers,
      defaultSchedulingFilters: args.defaultFilters,
      hasLoadAttemptStarted,
    }),
    [meetingsData, args.scopeType, args.scopeId, args.showBuffers, args.defaultFilters, hasLoadAttemptStarted]
  )
  return (
    <MeetingsPageSearchDataContext.Provider value={contextValue}>
      {!isInitialListLoading && <MeetingsPageContentBody />}
    </MeetingsPageSearchDataContext.Provider>
  )
}

const MeetingsPageContentBody = () => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const previousPathname = usePrevious(pathname)
  const searchDataContext = useContext(MeetingsPageSearchDataContext)
  const {
    meetingsData,
    scopeType,
    scopeId,
    showBuffers,
    defaultSchedulingFilters: filterDefaults,
    onVisibleDateRangeChange,
    connectionState,
    hasLoadAttemptStarted = false,
  } = searchDataContext ?? {}
  const isRealtimeFeatureFlagOn = useFlag('meetings_recaps_realtime_feature')
  const scopeKey = `${scopeType}-${scopeId}`
  const previousScopeKey = usePrevious(scopeKey)
  const selectedView = useSelectedView()
  const headerRef = useRef(null)
  const onboardingTourMeetingRef = useRef(null)
  const onboardingTourNotetakerRef = useRef(null)
  const onboardingTourSettingsRef = useRef(null)
  const [isMeetingRefReady, setMeetingRefReady] = useState(false)
  const [isNotetakerRefReady, setNotetakerRefReady] = useState(false)
  const [isSettingsRefReady, setSettingsRefReady] = useState(false)
  const onboardingTourMeetingRefCallback = useCallback((el) => {
    onboardingTourMeetingRef.current = el
    setMeetingRefReady(!!el)
  }, [])
  const onboardingTourNotetakerRefCallback = useCallback((el) => {
    onboardingTourNotetakerRef.current = el
    setNotetakerRefReady(!!el)
  }, [])
  const onboardingTourSettingsRefCallback = useCallback((el) => {
    onboardingTourSettingsRef.current = el
    setSettingsRefReady(!!el)
  }, [])
  const isFilterSettingsEnabled = useNewCode('sa_filter_settings_mtg_pg', false)?.enabled
  const isDefaultPresetToggleEnabled = useFlag('sa_default_preset_toggle', false)
  // Keep scroll anchoring + the scroll-to-today button aligned with the taller preset header.
  useStickyHeaderScrollOffset(headerRef, isDefaultPresetToggleEnabled)
  const isPhone = useDeviceType('isPhone')
  const scopeTitle = useSelector(titleSelector)
  const { getTabScopeV1 } = useHomeTabs()
  const { type: scopeV1Type, id: scopeV1Id } = getTabScopeV1()

  // On the preset mobile layout the scope control moves into the filter sheet, so it
  // is not mounted to load its own title. Load it here so the page header can show the
  // selected scope under the Calendar title. getTabScopeV1 returns a fresh object each
  // render, so depend on the primitive scope identifiers — depending on the function
  // would re-dispatch every render and SCOPE_DROPDOWN_TITLE_REQUEST would reset the
  // title to null, leaving the scope stuck in a perpetual loading state.
  useEffect(() => {
    if (!isDefaultPresetToggleEnabled || !isPhone) return

    dispatch(loadTitle(scopeV1Type, scopeV1Id))
  }, [dispatch, isDefaultPresetToggleEnabled, isPhone, scopeV1Type, scopeV1Id])
  const hasOpenedInitialMeeting = useRef(false)
  const justClearedScopeRef = useRef(false)
  const queryParams = useQueryParams()
  const {
    debug,
    selected,
    show_add_notetaker_modal: showAddNotetakerModalParam,
  } = queryParams
  const shouldShowAddNotetakerModal = showAddNotetakerModalParam === 'true'
  useRescheduledToast()
  const { replace, close } = useRightRail()
  const replaceRef = useRef(replace)
  const closeRef = useRef(close)
  replaceRef.current = replace
  closeRef.current = close

  useShouldDisplayAutoShareDiscoveryModal()

  // Track feature flag encounters that determine which meetings page to show. Update this when the flags change.
  const hasNewMeetingPage = useFlag('sa_new_meetings_page')
  useFeatureFlagEncountered(hasNewMeetingPage ? 'sa_new_meetings_page' : 'meeting_intelligence')
  useTempFeatureFlagEncountered('sa_new_meetings_page')

  useTrackPageExit({
    routePattern: MEETINGS_PAGE_TRACK_EXIT_MATCH,
    properties: { title: 'Meetings' }
  })

  const eventId = selected
  const featureKey = 'meeting_intelligence'
  const isFeatureAvailable = useSelector((state) => isAvailableFeatureSelector(state, { featureKey }))
  const isWizardAcknowledgementModalVisible = useSelector(
    (state) => state.meetingIntelligence?.acknowledgementModal?.showModal === true
  )

  const { presetFlagOn, viewDate } = useMeetingsViewDate()
  const { activePreset } = useUpcomingPreset()
  const { getFilterParams } = useFilters()
  const listQueryKey = useMemo(
    () => JSON.stringify({
      scopeKey,
      filterParams: getFilterParams(filterDefaults),
      activePreset,
      viewDate,
    }),
    [scopeKey, getFilterParams, filterDefaults, activePreset, viewDate],
  )

  const onScrollAnchorDateChange = useCallback((date) => {
    if (!presetFlagOn) return
    dispatch(setScrollAnchorDate(date))
  }, [presetFlagOn, dispatch])

  const selectedMeeting = useMemo(() => {
    if (!eventId || !meetingsData?.data?.length) {
      return null
    }
    for (const day of meetingsData.data) {
      const match = day.events?.find((event) => event?.uuid === eventId)
      if (match) {
        return match
      }
    }
    return null
  }, [eventId, meetingsData?.data])

  // Date (YYYY-MM-DD) of the meeting open in the right rail, so the date selector label can
  // reflect where the user is in the feed. Only tracked behind the date-selector flag so the
  // page is fully inert when it is off. Null when nothing is open.
  const openMeetingDate = useMemo(() => {
    if (!presetFlagOn || !eventId || !meetingsData?.data?.length) return null
    for (const day of meetingsData.data) {
      if (day.events?.some((event) => event?.uuid === eventId)) return day.rawDate ?? null
    }
    return null
  }, [presetFlagOn, eventId, meetingsData?.data])

  // Record the open meeting's date, and keep it after the rail closes so the selector stays
  // focused on where the user was. It is reset to today only by an explicit date pick or preset
  // toggle (which clear the slice), and cleared on page unmount below.
  useEffect(() => {
    if (!presetFlagOn || !openMeetingDate) return
    dispatch(setOpenMeetingDate(openMeetingDate))
  }, [presetFlagOn, dispatch, openMeetingDate])

  useEffect(() => () => {
    dispatch(setOpenMeetingDate(null))
    dispatch(setScrollAnchorDate(null))
  }, [dispatch])

  const clearedInvalidSelectedRef = useRef(null)

  // When meeting is not in the list, only clear the add-notetaker modal flag.
  // Do not clear selected here: the right rail stays open so the meeting
  // detail provider can run REST then GraphQL fallback; it will clear selected when both fail.
  useEffect(() => {
    if (!eventId || !meetingsData?.data?.length) return
    const found = meetingsData.data.some((day) => day.events?.some((event) => event?.uuid === eventId))
    if (found) return
    if (clearedInvalidSelectedRef.current === eventId) return
    clearedInvalidSelectedRef.current = eventId
    dispatch(setGetParams({ show_add_notetaker_modal: null }))
  }, [eventId, meetingsData?.data, dispatch])

  const refreshMeeting = useCallback((meetingUuid) => {
    if (meetingsData?.refreshMeeting) {
      meetingsData.refreshMeeting(meetingUuid)
    }
  }, [meetingsData])

  const clearActiveMeeting = useCallback(() => {
    dispatch(setGetParams({ selected: null }))
  }, [dispatch])

  const handleCloseAddNotetakerModal = useCallback(() => {
    dispatch(setGetParams({ show_add_notetaker_modal: null }))
  }, [dispatch])

  // When user/scope changes (pathname or scope dropdown), clear right rail and selection immediately.
  // useLayoutEffect so this runs before other effects; set justClearedScopeRef so we don't re-open
  // (replace would re-spread current URL params and put selected back).
  // Also call close() to sync the rail's internal stack - prevents "Maximum update depth exceeded"
  // when replace/close refs change after URL update and re-trigger effects.
  //
  // Exception: when URL has selected, do NOT clear. Scope can change spuriously when the
  // meetings list finishes loading (scopeKey may settle or re-derived values trigger scopeChanged).
  // User-initiated scope change strips selected before navigating (setTabScopeV1), so we never
  // have selected during a real scope switch. If we have selected, the provider is loading/showing
  // that meeting - don't close the rail.
  useLayoutEffect(() => {
    const pathChanged = previousPathname !== undefined && previousPathname !== pathname
    const scopeChanged = previousScopeKey !== undefined && previousScopeKey !== scopeKey
    if (!pathChanged && !scopeChanged) return

    if (selected) {
      justClearedScopeRef.current = false
      return
    }

    justClearedScopeRef.current = true
    closeRef.current({ all: true })
    const prefix = URL_STATE_PREFIX.RIGHT_RAIL
    dispatch(setGetParams({
      [prefix]: null,
      [`${prefix}State`]: null,
      selected: null,
    }))
  }, [pathname, previousPathname, scopeKey, previousScopeKey, dispatch, selected])

  // Open rail when we have a selection; reset "opened" flag when selection is cleared so the next
  // selection in this scope will open the rail again.
  // Use refs for replace/close - they change when URL updates (e.g. on scope clear), which
  // would re-trigger this effect and potentially re-open the rail, causing infinite update loop.
  useEffect(() => {
    if (!eventId) {
      hasOpenedInitialMeeting.current = false
      return
    }
    if (justClearedScopeRef.current) {
      justClearedScopeRef.current = false
      return
    }
    if (!hasOpenedInitialMeeting.current) {
      replaceRef.current('meeting_details', { eventId })
      hasOpenedInitialMeeting.current = true
    }
  }, [eventId])

  useOnValueChange(eventId, () => {
    if (eventId) {
      if (justClearedScopeRef.current) {
        justClearedScopeRef.current = false
        return
      }
      replaceRef.current('meeting_details', { eventId })
    } else {
      closeRef.current()
    }
  })

  const showMeetingsDebugMenu = isRealtimeFeatureFlagOn && debug === 'true'
  const displayedMeetingCount = useStableDisplayedMeetingCount({
    dateGroups: meetingsData?.data,
    isLoading: meetingsData?.isLoading,
    isNextLoading: meetingsData?.isNextLoading,
    isPreviousLoading: meetingsData?.isPreviousLoading,
    resetKey: listQueryKey,
  })
  const headerActions = [
    ...(FreeRecapsHeaderAction ? (
      [<FreeRecapsHeaderAction key="free-recaps-header-action" paywallLocation={BILLING_ENTRYPOINTS.MEETINGS_PAGE} />]
    ) : []),
    ...(showMeetingsDebugMenu ? [<MeetingsDebugMenu key="meetings-debug-menu" connectionState={connectionState} />] : []),
    ...(isDefaultPresetToggleEnabled ? [
      <MeetingsPageHeaderActions
        key="meetings-page-header-actions"
        scope={{ id: scopeId, type: scopeType }}
        canExport
        filterDefaults={filterDefaults}
        onboardingTourSettingsRef={onboardingTourSettingsRefCallback}
      />,
    ] : []),
  ]

  const headerString = useProductRefreshAppShell()
    ? I18n.t('publisher.personal_settings.calendar_section.title') : I18n.t('publisher.meetings.header')
  const pageHeaderDescription = isDefaultPresetToggleEnabled && isPhone && scopeTitle
    ? scopeTitle
    : undefined

  return (
    <MeetingsProvider meetings={meetingsData.data}>
      <PageHeader
        title={headerString}
        description={pageHeaderDescription}
        actions={headerActions}
      />
      {!isFeatureAvailable && <DismissableNotetakerBanner bannerType="meetings" featureKey={featureKey} />}
      <SearchFilterExport
        scope={{ id: scopeId, type: scopeType }}
        showBuffers={showBuffers}
        selectedView={selectedView}
        canExport
        ref={headerRef}
        clearActiveMeeting={clearActiveMeeting}
        filterDefaults={filterDefaults}
        onboardingTourSettingsRef={onboardingTourSettingsRefCallback}
        displayedMeetingCount={displayedMeetingCount}
      />
      <InnerContent
        showBuffers={showBuffers}
        headerRef={headerRef}
        hasLoadAttemptStarted={hasLoadAttemptStarted}
        selectedMeetingId={eventId}
        filterDefaults={filterDefaults}
        onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback}
        onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback}
        onVisibleDateRangeChange={onVisibleDateRangeChange}
        onScrollAnchorDateChange={onScrollAnchorDateChange}
        scrollToDateTarget={viewDate}
        {...meetingsData}
      />
      {selectedMeeting && shouldShowAddNotetakerModal && (
        <AddNotetakerModal
          isOpen={shouldShowAddNotetakerModal}
          onClose={handleCloseAddNotetakerModal}
          onMeetingUpdated={refreshMeeting}
          selectedMeeting={selectedMeeting}
        />
      )}
      <AutoShareDiscoveryModal />
      <MeetingsPageOnboardingTour
        onboardingTourMeetingRef={onboardingTourMeetingRef}
        onboardingTourNotetakerRef={onboardingTourNotetakerRef}
        onboardingTourSettingsRef={onboardingTourSettingsRef}
        isMeetingRefReady={isMeetingRefReady}
        isNotetakerRefReady={isNotetakerRefReady}
        isSettingsRefReady={isSettingsRefReady}
        isDefaultPresetToggleEnabled={isDefaultPresetToggleEnabled}
        isFilterSettingsEnabled={isFilterSettingsEnabled}
        isWizardAcknowledgementModalVisible={isWizardAcknowledgementModalVisible}
      />
    </MeetingsProvider>
  )
}

/** Picks realtime (SSE + polling) vs legacy (no SSE, no polling) branch. */
const MeetingsPageContent = () => {
  const hasRealtimeFeaturesEnabled = useMeetingsRealtimeFeaturesEnabled()
  return hasRealtimeFeaturesEnabled
    ? <MeetingsPageContentWithRealtime />
    : <MeetingsPageContentWithoutRealtime />
}

export const MeetingsPage = () => {
  const { params } = useRouteMatch()
  const basePath = params[0]
  const { isLoading: isLoadingSettings, isError: isSettingsError } = useGetUserSettingsQuery()

  // Gate only on the very first settings load to avoid multiple search API calls.
  // Once settings have resolved (success or error), never show this loader again —
  // subsequent cache invalidations / refetches must not unmount MeetingsPageContent,
  // which would restart all queries and cause the meeting list to remount + scroll.
  const hasSettingsResolved = useRef(false)
  if (!isLoadingSettings || isSettingsError) {
    hasSettingsResolved.current = true
  }

  const isSettingsLoading = !hasSettingsResolved.current
  useMeetingsTabPageLoad(isSettingsLoading)

  if (isSettingsLoading) {
    return null
  }

  return (
    <Switch>
      <Route exact path={`${basePath}/user/me`}>
        <MeetingsPageContent />
      </Route>
      <Route exact path={`${basePath}/team`}>
        <AllPagesAuthorization>
          <MeetingsPageContent />
        </AllPagesAuthorization>
      </Route>
      <Route exact path={`${basePath}/all_users_and_teams`}>
        <AllUsersAuthorization>
          <MeetingsPageContent />
        </AllUsersAuthorization>
      </Route>
      <Route exact path={`${basePath}/all_users`}>
        <AllUsersAuthorization>
          <MeetingsPageContent />
        </AllUsersAuthorization>
      </Route>
      <Route exact path={`${basePath}/team/:team_id`}>
        <PageAuthorization>
          <MeetingsPageContent />
        </PageAuthorization>
      </Route>
      <Route exact path={`${basePath}/user_group/:user_group_id`}>
        <UserGroupAuthorization>
          <MeetingsPageContent />
        </UserGroupAuthorization>
      </Route>
      <Route exact path={`${basePath}/user/:user_id`}>
        <UserAuthorization>
          <MeetingsPageContent />
        </UserAuthorization>
      </Route>
      <Route>
        <MeetingsPageContent />
      </Route>
    </Switch>
  )
}
